import {
  Box,
  Code,
  Flex,
  HStack,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  colors,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  LegacyClaim,
  objectToArray,
  PregnancyClaim,
  UpdateCallback,
  WithId,
} from '@hb/shared'
import { deleteDoc, deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'

import React, { useCallback, useContext, useMemo, useState } from 'react'

import { usePopUpMessage } from '@hb/react'
import { CLAIMS_REF } from '../../../../backend/db/collections'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useUpdateDoc } from '../../../../hooks/backend/useUpdateDoc'
import { ActionButton, DeleteButton } from '../../../Buttons'
import { FloatingContainer } from '../../../Containers'
import { DataView, GenericEditModal } from '../../../DataView'
import { NoteForm } from '../../../forms/NoteForm'
import { DefaultModal } from '../../../Modals/DefaultModal'
import { AssessmentDocuments } from '../Documents/AssessmentDocuments'
import { DraftView } from '../Documents/DraftView'
import { ReferenceNumbersEdit } from '../ReferenceNumberEdit'
import { AbilityClaimView } from './Ability/AbilityClaim'
import { serviceTypeField, serviceTypeOtherField } from './Ability/fields'
import { InovalonButton } from './Ability/InovalonButton'
import { LinkAbilityClaim } from './Ability/LinkAbilityClaim'
import { ClaimSubmissionForm } from './Ability/SubmitNewClaim/ClaimSubmissionForm'
import {
  AbilityClaimSubmission,
  ResubmitAbilityClaim,
} from './Ability/SubmitNewClaim/SubmitClaimModal'
import { ClaimView } from './ClaimView'
import { status } from './fields'

interface ClaimsViewData {
  selectedId: string | null
  select: (id: string | null) => void
  updatingAbilityId: string | null
  setUpdatingAbilityId: (id: string | null) => void
  creatingNewLegacy: boolean
  setCreatingNewLegacy: (val: boolean) => void
  creatingNewAbilityClaim: boolean
  setCreatingNewAbilityClaim: (val: boolean) => void
  isLinking: boolean
  setIsLinking: (val: boolean) => void
}

const ClaimsViewContext = React.createContext<ClaimsViewData>({
  selectedId: null,
  select: () => {},
  updatingAbilityId: null,
  setUpdatingAbilityId: () => {},
  creatingNewLegacy: false,
  setCreatingNewLegacy: () => {},
  creatingNewAbilityClaim: false,
  setCreatingNewAbilityClaim: () => {},
  isLinking: false,
  setIsLinking: () => {},
})

export const toClaimName = (data: FieldMapValue) =>
  `${data?.serviceType?.toString().toUpperCase() || 'NO SERVICE TYPE'}${
    data.serviceType === 'other' && data.serviceTypeOther ? ` (${data.serviceTypeOther})` : ''
  }: ${data.status?.toString().toUpperCase()} - ${
    data.serviceDates || 'No dates of service'
  } - ${data.cptCodes || 'No cpt codes'}`

const useSaveLegacyClaim = (selectedId: string | null, select: (id: string) => void) => {
  const {
    user,
    assessmentId,
    selectedAssessment: { populated },
  } = useProfile()
  return useCallback(
    async (data: FieldMapValue): Promise<UpdateCallback> => {
      if (!user?.id) return { error: 'no user selected' }
      if (!selectedId) return { error: 'no claim selected' }
      if (!assessmentId) return { error: 'no assessment selected' }
      try {
        const claim: Partial<PregnancyClaim> = {
          legacy: data as LegacyClaim,
          patientId: user.id,
          assessmentId,
          status: null,
          archived: !!populated?.archived,
          assessmentOnClaimsList: !!populated?.adminStages.includes('Claims'),
          assessmentOnComplaintsList: !!populated?.adminStages.includes('Complaints'),
          assessmentStages: populated?.adminStages ?? [],
          // assessmentHasComplaints: populated?.hasComplaints ?? false,
          log: populated?.log ?? {},
          assignedAdmins: populated?.assignedAdmins ?? [],
          forceUpdatedOn: Date.now(),
          stringified: populated?.stringified ?? '',
          isUrgent: populated?.isUrgent ?? false,
          authRefNumbers: populated?.authRefNumbers ?? [],
          claimRefNumbers: populated?.claimRefNumbers ?? [],
          eftCheckRefNumbers: populated?.eftCheckRefNumbers ?? [],
          dobString: populated?.dobString ?? '',
          nextActionsString: populated?.nextActionsString ?? '',
          urgentReason: populated?.urgentReason ?? '',
        }
        const newRef = selectedId === 'new' ? doc(CLAIMS_REF) : doc(CLAIMS_REF, selectedId)
        await setDoc(newRef, claim, { merge: true })
        select(newRef.id)
        return { success: 'Saved' }
      } catch (err: any) {
        return { error: err.message }
      }
    },
    [selectedId, user, assessmentId, select, populated],
  )
}

const claimField: FieldMap = {
  name: 'Claim',
  toName: toClaimName,
  children: {
    serviceType: serviceTypeField,
    serviceTypeOther: serviceTypeOtherField,
    status,
    submissionDate: {
      type: FieldTypes.DATE,
      placeholder: 'Submission date',
    },
    serviceDates: {
      optional: true,
      type: FieldTypes.TEXTAREA,
      placeholder: 'Date(s) of service...',
    },
    cptCodes: {
      optional: true,
      type: FieldTypes.TEXTAREA,
      placeholder: 'CPT Codes/Billed Charges',
    },
    // billedCharges: {
    //   optional: true,
    //   type: FieldTypes.TEXTAREA,
    //   placeholder: 'Billed charges',
    // },
    sessionNumber: {
      optional: true,
      type: FieldTypes.TEXT,
      placeholder: 'Session #',
    },
    claimNumber: {
      optional: true,
      type: FieldTypes.TEXT,
      placeholder: 'Claim #',
    },
    notes: {
      type: FieldTypes.TEXTAREA,
      optional: true,
      placeholder: 'Notes...',
    },
  },
}

const ClaimModal = ({
  selectedId,
  selected,
  select,
  onClose,
}: {
  selectedId: string | null
  selected?: WithId<PregnancyClaim>
  select: (id: string) => void
  onClose: () => void
}) => {
  const { processResponse } = usePopUpMessage()
  const onSave = useSaveLegacyClaim(selectedId, select)

  const onDeleteLegacy = useCallback(async (): Promise<UpdateCallback> => {
    if (!selectedId) return { error: 'no claim selected' }
    try {
      const docRef = doc(CLAIMS_REF, selectedId)
      const currentDoc = await getDoc(docRef)
      const currentData = currentDoc.data() as PregnancyClaim | undefined
      if (currentData?.abilityId) {
        await updateDoc(docRef, 'legacy', deleteField())
      } else await deleteDoc(doc(CLAIMS_REF, selectedId))
      onClose()
      return { success: 'Claim deleted' }
    } catch (err: any) {
      console.error(err)
      return { error: err?.message || 'Error deleting claim' }
    }
  }, [selectedId, onClose])
  const submittedToAbility = useMemo(() => selected?.submittedToAbility, [selected])
  return (
    <DefaultModal
      size="4xl"
      bodyProps={{ minHeight: '440px' }}
      contentProps={{ bg: 'gray.50' }}
      overlayHeader
      isOpen={!!selectedId}
      onClose={onClose}
      render={() => (
        <Tabs h="100%" isLazy defaultIndex={selectedId === 'new' || !selected?.abilityId ? 1 : 0}>
          <TabList bg="white" pt={1}>
            <Tab>CLAIM</Tab>
            {selected?.submittedArgs ? <Tab>SUBMISSION FORM</Tab> : null}
            {submittedToAbility ? <Tab>SUBMITTED TO ABILITY</Tab> : null}
            <Tab>LEGACY</Tab>
            <Tab>DELETE CLAIM</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={3}>
              <AbilityClaimView withMore claimId={selectedId} claim={selected} />
            </TabPanel>
            {selected?.submittedArgs ? (
              <TabPanel p={0}>
                <ClaimSubmissionForm claim={selected} />
              </TabPanel>
            ) : null}
            {submittedToAbility ? (
              <TabPanel p={0}>
                <Code minW="100%" p={4} whiteSpace="pre-wrap">
                  {JSON.stringify(submittedToAbility, null, 2)}
                </Code>
              </TabPanel>
            ) : null}
            <TabPanel padding={0}>
              <DataView
                initEditing={selectedId === 'new'}
                onSubmit={onSave}
                boxProps={{ px: 2 }}
                withEditModal
                field={claimField}
                onEditingChange={() => {
                  if (selectedId === 'new') onClose()
                }}
                onDelete={async () => {
                  if (selectedId === 'new') {
                    onClose()
                    return { success: 'true' }
                  }
                  return onDeleteLegacy()
                }}
                data={selectedId === 'new' ? { submissionDate: Date.now() } : selected?.legacy}
              />
            </TabPanel>
            <TabPanel>
              <Flex w="100%" flexFlow="column" gap={1}>
                <Text px={2} fontSize="lg" color="gray.600">
                  This will delete the claim from the Hamilton Billing system, but not from Inovalon
                </Text>
                <Flex>
                  <DeleteButton
                    itemName="claim"
                    text="DELETE CLAIM"
                    size="md"
                    onDelete={async () => {
                      if (!selectedId) return
                      try {
                        await deleteDoc(doc(CLAIMS_REF, selectedId))
                        onClose()
                      } catch (err: any) {
                        processResponse({
                          error: `Error deleting claim: ${err.message}`,
                        })
                      }
                    }}
                  />
                </Flex>
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    />
  )
}

const LinkClaimModal = ({ onClose }: { onClose: () => void }) => {
  const { assessmentId } = useProfile()
  return (
    <DefaultModal
      overlayHeader
      isOpen
      onClose={onClose}
      render={() => (
        <VStack p={3} w="100%">
          <LinkAbilityClaim
            assessmentId={assessmentId ?? ''}
            alwaysOpen
            claimId={null}
            onCancel={onClose}
          />
        </VStack>
      )}
    />
  )
}

const NewClaimModal = ({
  select,
  isOpen,
  onClose,
}: {
  select: (id: string) => void
  isOpen: boolean
  onClose: () => void
}) => {
  const { assessmentId, user } = useProfile()
  const onSave = useCallback(
    async (data: FieldMapValue): Promise<UpdateCallback> => {
      if (!user?.id) return { error: 'no user selected' }
      if (!assessmentId) return { error: 'no assessment selected' }
      try {
        const newRef = doc(CLAIMS_REF)
        await setDoc(newRef, { legacy: data, patientId: user.id, assessmentId }, { merge: true })
        select(newRef.id)
        return { success: 'Saved' }
      } catch (err: any) {
        return { error: err.message }
      }
    },
    [user, assessmentId, select],
  )

  return <GenericEditModal field={claimField} onSubmit={onSave} isOpen={isOpen} onClose={onClose} />
}

const ClaimsTabSubmitClaim = ({
  existingAbilityId,
  claim,
  onClose,
}: {
  existingAbilityId?: string | null
  claim: PregnancyClaim | undefined
  onClose: () => void
}) =>
  existingAbilityId ? (
    <ResubmitAbilityClaim
      serviceType={claim?.serviceType}
      existingAbilityId={existingAbilityId}
      onClose={onClose}
    />
  ) : (
    <AbilityClaimSubmission onClose={onClose} />
  )

const ClaimsTabContent = () =>
  //   onLegacyCreate,
  //   onClaimSubmit,
  //   onLinkClaim,
  //   selectedClaimId,
  //   onSelectClaim,
  // }: {
  //   onLegacyCreate: () => void
  //   onClaimSubmit: () => void
  //   onLinkClaim: () => void
  //   selectedClaimId: string | null
  //   onSelectClaim: (id: string | null) => void
  // }) => {
  {
    const {
      selectedId: selectedClaimId,
      select: onSelectClaim,
      setCreatingNewLegacy,
      setCreatingNewAbilityClaim,
      setIsLinking,
    } = useContext(ClaimsViewContext)
    const {
      selectedAssessment: { adminRef, populated: selectedAssessment },
      assessmentId,
      claims: { data: claims },
      isInvite,
    } = useProfile()
    const { claimsNote, patientId, drafts, plans } = selectedAssessment ?? {}
    const sortedClaims = useMemo(
      () =>
        objectToArray(claims ?? {}).sort((a, b) => {
          if (!a.abilitySnippet && !b.abilitySnippet) return 0
          if (!a.abilitySnippet) return 1
          if (!b.abilitySnippet) return -1
          const aDate = a.abilitySnippet.CreateDate
            ? new Date(a.abilitySnippet.CreateDate).getTime()
            : 0
          const bDate = b.abilitySnippet.CreateDate
            ? new Date(b.abilitySnippet.CreateDate).getTime()
            : 0
          return bDate - aDate
        }),
      [claims],
    )
    const selected = useMemo(() => {
      const selectedClaim = selectedClaimId ? claims?.[selectedClaimId] : undefined
      if (!selectedClaim) return undefined
      return { ...selectedClaim, id: selectedClaimId ?? '' }
    }, [selectedClaimId, claims])

    const insuranceProvider = plans?.primary?.insuranceProvider

    const update = useUpdateDoc()
    return (
      <VStack spacing={0} px={2} align="flex-start" w="100%">
        <FloatingContainer style={{ padding: 0 }} bg="white" w="100%">
          <Stack px={4} pt={4} pb={3} bg={`${colors.pink.hex}88`} pos="relative" w="100%">
            <HStack w="100%">
              <ActionButton
                flex={1}
                size="sm"
                bg="white"
                color="green.500"
                fontFamily="Hero-New"
                fontWeight={500}
                onClick={() => setCreatingNewLegacy(true)}>
                + ADD CLAIM (LEGACY)
              </ActionButton>
              <InovalonButton onClick={() => setIsLinking(true)}>LINK CLAIM</InovalonButton>
              <InovalonButton onClick={() => setCreatingNewAbilityClaim(true)}>
                SUBMIT CLAIM
              </InovalonButton>
            </HStack>
            <VStack w="100%">
              {sortedClaims.map(claim => (
                <ClaimView
                  id={claim.id}
                  key={claim.id}
                  onClick={() => onSelectClaim(claim.id)}
                  claim={claim}
                />
              ))}
            </VStack>
          </Stack>
        </FloatingContainer>
        <ReferenceNumbersEdit id="claimsReferenceNumbers" />
        <NoteForm
          note={claimsNote}
          boxProps={{ mt: 2 }}
          onSubmit={data => update(adminRef, 'claimsNote', data)}
        />
        <Flex py={2} gap={2} flexFlow="column" w="100%">
          <DraftView name="Claims Template" templateKey="claims" draft={drafts?.claimTemplates} />
          {assessmentId && patientId ? (
            <AssessmentDocuments
              adminView
              isInvite={isInvite}
              patientId={patientId}
              category="claims"
              assessmentId={assessmentId}
            />
          ) : null}
        </Flex>
        <Box width="100%" p={2}>
          <Text fontWeight={600} color="gray.700">
            {insuranceProvider ? `${insuranceProvider.name} ` : 'Insurer '}
            Claims Cheat Sheet
          </Text>
          <Text
            fontStyle={insuranceProvider?.claimsSheet ? 'normal' : 'italic'}
            color={insuranceProvider?.claimsSheet ? 'black' : 'gray.700'}
            whiteSpace="pre-wrap">
            {insuranceProvider
              ? (insuranceProvider.claimsSheet ??
                `No claims cheat sheet for ${insuranceProvider.name}`)
              : 'No insurance provider'}
          </Text>
        </Box>
        {selectedClaimId ? (
          <ClaimModal
            select={onSelectClaim}
            selected={selected}
            selectedId={selectedClaimId}
            onClose={() => onSelectClaim(null)}
          />
        ) : null}
      </VStack>
    )
  }

export const AssessmentClaims = () => {
  const [creatingNewAbilityClaim, setCreatingNewAbilityClaim] = useState(false)
  const [isLinking, setIsLinking] = useState(false)

  const [creatingNewLegacy, setCreatingNewLegacy] = useState(false)
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null)
  const [updatingAbilityId, setUpdatingAbilityId] = useState<string | null>(null)

  const { claims } = useProfile()
  const updatingAbilityClaim = useMemo(() => {
    if (!updatingAbilityId) return undefined
    return (
      Object.values(claims.data ?? {}).find(c => c.abilityId === updatingAbilityId) ?? undefined
    )
  }, [updatingAbilityId, claims])

  const contextData = useMemo<ClaimsViewData>(
    () => ({
      creatingNewAbilityClaim,
      setCreatingNewAbilityClaim,
      select: setSelectedClaimId,
      selectedId: selectedClaimId,
      creatingNewLegacy,
      setCreatingNewLegacy,
      isLinking,
      setIsLinking,
      updatingAbilityId,
      setUpdatingAbilityId,
    }),
    [
      selectedClaimId,
      setSelectedClaimId,
      setUpdatingAbilityId,
      updatingAbilityId,
      creatingNewAbilityClaim,
      creatingNewLegacy,
      isLinking,
      setIsLinking,
    ],
  )

  return (
    <ClaimsViewContext.Provider value={contextData}>
      <VStack px={2} py={3} pos="relative" w="100%">
        {creatingNewAbilityClaim || updatingAbilityId ? (
          <ClaimsTabSubmitClaim
            claim={updatingAbilityClaim}
            onClose={() => {
              setCreatingNewAbilityClaim(false)
              setUpdatingAbilityId(null)
              setSelectedClaimId(null)
            }}
            existingAbilityId={updatingAbilityId}
          />
        ) : (
          <ClaimsTabContent />
        )}
        {creatingNewLegacy ? (
          <NewClaimModal
            isOpen
            onClose={() => setCreatingNewLegacy(false)}
            select={setSelectedClaimId}
          />
        ) : null}
        {isLinking ? <LinkClaimModal onClose={() => setIsLinking(false)} /> : null}
      </VStack>
    </ClaimsViewContext.Provider>
  )
}
